import React from "react";
import { createPortal } from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import { withFormsy } from "formsy-react";
import classNames from "classnames";
import moment from "moment-mini";

import connect from "../../../i18n/connect";
import InputText from "../../../web/components/common/inputs/InputText/InputText";
import detectDateFormat from "../../detectDateFormat";
import FormsyBase from "./FormsyBase";
import transformDateTimeIntoDate from "../../transformDateTimeIntoDate";
import pl from "date-fns/locale/pl";
import en from "date-fns/locale/en-US";

import { DATE_PICKER_YEAR_DROPDOWN_ITEM_NUMBER } from "./datePickerYearDropdownItemNumber";
import "./FormsyDatePicker.scss";

registerLocale("pl", pl);
registerLocale("en", en);

@connect()
@withFormsy
class FormsyComposedNewDateField extends FormsyBase {
  state = {
    blurred: false,
    isOpen: false,
    position: { x: 0, y: 0 }
  };

  constructor(props) {
    super(props);
    this.calendarButtonRef = React.createRef();
  }

  setIsOpen(value) {
    this.setState({
      isOpen: value
    });
  }

  showError() {
    const { isValid, isPristine, skipPristine, isFormSubmitted } = this.props;
    const { blurred } = this.state;
    return (
      !isValid &&
      (!isPristine || skipPristine) &&
      (blurred || skipPristine || isFormSubmitted)
    );
  }

  onBlur = event => {
    const { blurred } = this.state;

    if (!blurred) {
      this.setState({ blurred: true });
    }

    if (this.props.onBlur && typeof this.props.onBlur === "function") {
      this.props.onBlur(event);
    }
  };

  getSelectedDate() {
    const { value } = this.props;

    let selectedDate = moment(value, detectDateFormat(value)).toDate();

    if (!this.isValidDate(selectedDate)) {
      selectedDate = new Date();
    }

    return selectedDate;
  }

  isValidDate(date) {
    return date instanceof Date && !isNaN(date);
  }

  handleCalendarButtonClick = () => {
    this.setIsOpen(true);

    const rect = this.calendarButtonRef.current.getBoundingClientRect();
    const calendarWidth = 270;
    const calendarHeight = 300;
    const offset = 10;

    let x = rect.left + window.scrollX;
    let y = rect.bottom + window.scrollY;

    const maxX = window.innerWidth - calendarWidth - offset;
    if (x > maxX) {
      x = maxX;
    }

    const maxY = window.innerHeight - calendarHeight - offset;
    if (rect.bottom > maxY) {
      y = rect.top + window.scrollY - calendarHeight - offset;
    }

    this.setState({
      position: { x, y }
    });
  };

  render() {
    const {
      name,
      label,
      value,
      translate,
      locale,
      targetFormat,
      className,
      allowFuture,
      formatDatetimeLikeValue,
      errorMessage,
      disabled,
      selectTextOnFocus = false
    } = this.props;

    const { isOpen } = this.state;

    const formattedValue = formatDatetimeLikeValue
      ? transformDateTimeIntoDate(value, targetFormat)
      : value;

    const showError = this.showError();

    const fmt =
      targetFormat && typeof targetFormat === "string"
        ? targetFormat
        : "DD.MM.YYYY";

    return (
      <div
        className={classNames({
          "datepicker-with-calendar-button-wrapper": true,
          "formsy-composed-new-date-field": true,
          [className]: !!className
        })}
      >
        <div className="formsy-datepicker-wrapper">
          <InputText
            name={name}
            label={translate(label)}
            value={formattedValue || ""}
            onChange={newValue => this.handleChange(newValue)}
            onBlur={this.onBlur}
            errorMessage={showError ? translate(errorMessage) : null}
            disabled={disabled}
            selectTextOnFocus={selectTextOnFocus}
            endChild={
              <button
                type="button"
                ref={this.calendarButtonRef}
                className="lp_input-text_end-btn-icon"
                onClick={this.handleCalendarButtonClick}
                disabled={disabled}
                aria-label={translate("Calendar")}
              >
                <i className="fal fa-calendar-alt" aria-hidden={true} />
              </button>
            }
          />
          {createPortal(
            <div
              className={"formsy-datepicker-portal-warpper"}
              style={{
                display: isOpen ? "block" : "none",
                top: this.state.position.y,
                left: this.state.position.x
              }}
            >
              <DatePicker
                locale={locale}
                name={name}
                selected={this.getSelectedDate()}
                allowSameDay={true}
                maxDate={allowFuture ? undefined : new Date()}
                dateFormat={"dd.MM.yyyy"}
                inline={isOpen}
                withPortal
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={DATE_PICKER_YEAR_DROPDOWN_ITEM_NUMBER}
                onChange={date => {
                  this.handleChange(moment(date).format(fmt));
                  this.setIsOpen(false);
                }}
                onClickOutside={() => {
                  this.setIsOpen(false);
                }}
                customInput={<div />}
              />
            </div>,
            window.document.querySelector(".app-content")
          )}
        </div>
      </div>
    );
  }
}

export default FormsyComposedNewDateField;
